"use client";

import { useContext, useEffect, useState } from "react";
import type { BannerStoryblok } from "@/component-types-sb";
import { CampsiteContext } from "@/lib/context/CampsiteContext";
import { getCookie } from "@/lib/hooks/useCookie";
import { isBannerEligible } from "@/lib/storyblok/banner";
import BannerItem, { type BannerDismissReason } from "./BannerItem";

type BannerPops = {
  blok: BannerStoryblok;
};

const BannerCarousel = ({
  blok,
  campsiteId,
}: BannerPops & { campsiteId: string }) => {
  const [ready, setReady] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>();

  useEffect(() => setReady(true), []);

  const items = ready
    ? (blok.content || []).filter(
        (item) =>
          isBannerEligible(item, campsiteId) &&
          !dismissedIds.includes(item._uid) &&
          getCookie(item._uid) !== "true"
      )
    : [];
  const index = Math.max(
    0,
    items.findIndex((item) => item._uid === selectedId)
  );
  const activeItem = items[index];

  if (!activeItem) return null;

  const select = (offset: number) => {
    setSelectedId(items[(index + offset + items.length) % items.length]._uid);
  };

  const dismiss = (reason: BannerDismissReason) => {
    // Scrolling hides the automatic notices together, without setting cookies.
    const ids =
      reason === "scroll"
        ? items.filter((item) => item.mode === "auto").map((item) => item._uid)
        : [activeItem._uid];
    setDismissedIds((previous) => [...previous, ...ids]);
    const next = [...items.slice(index + 1), ...items.slice(0, index)].find(
      (item) => !ids.includes(item._uid)
    );
    setSelectedId(next?._uid);
  };

  return (
    <BannerItem
      blok={activeItem}
      onDismiss={dismiss}
      navigation={
        items.length > 1
          ? {
              index,
              count: items.length,
              onPrevious: () => select(-1),
              onNext: () => select(1),
            }
          : undefined
      }
    />
  );
};

const Banner = ({ blok }: BannerPops) => {
  const { id } = useContext(CampsiteContext);
  return <BannerCarousel key={id} blok={blok} campsiteId={id} />;
};

export default Banner;
